<template>
    <div>
        <CCol sm="12">
            <CCard>
                <CCardBody class="d-flex flex-column">
                    <div class="d-flex mb-2 justify-content-end align-items-start">
                        <!-- Title -->
                        <p class="flex-grow-1">{{ $t('project') }}</p>
                        <!-- Language dropdown -->
                        <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                    </div>
                    <!-- Form -->
                    <div class="row">
                        <!-- Image -->
                        <div class="col-12 col-lg-5">
                            <div role="group" class="form-group">
                                <label class=""> {{ $t('imageMandatory') }}</label>
                                <GalleryPicker class="gallery-picker" v-model="project.image" :ratioWidth="1"
                                    :ratioHeight="1"
                                    v-bind:class="{ 'is-invalid': genericValidator(project.image, attemptedSubmit) === false || errorValidator('Image') === false }"
                                    :max="1"></GalleryPicker>
                            </div>
                        </div>
                        <div class="col-12 col-lg-7">
                            <div class="row">
                                <div class="col-6">
                                    <!-- Names -->
                                    <div role="group" class="form-group">
                                        <label class=""> {{ $t('namesMandatory') }}</label>
                                        <CInput type="text" v-model="project.names[currentLanguage.key]"
                                            :invalid-feedback="errorOutput['Names']"
                                            :is-valid="genericValidator(project.names, attemptedSubmit) && errorValidator('Names')">
                                        </CInput>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <!-- Client -->
                                    <div role="group" class="form-group">
                                        <label class=""> {{ $t('clientMandatory') }}</label>
                                        <CInput type="text" v-model="project.client"
                                            :invalid-feedback="errorOutput['Client']"
                                            :is-valid="genericValidator(project.client, attemptedSubmit) && errorValidator('Client')">
                                        </CInput>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <!-- Services -->
                                    <div role="group" class="form-group">
                                        <label class=""> {{ $t('servicesMandatory') }}</label>
                                        <OptionsDropdown v-model="project.services" :options="servicesOptions"
                                            :title="$t('services')" :index="0"
                                            :is-valid="genericValidator(project.services, attemptedSubmit) && errorValidator('Services')"
                                            :invalid-feedback="errorOutput['Services']"></OptionsDropdown>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-12">
                            <!-- Descriptions -->
                            <div role="group" class="form-group">
                                <label class=""> {{ $t('descriptionsMandatory') }}</label>
                                <vue-editor v-model="project.descriptions[currentLanguage.key]"
                                    v-bind:class="{ 'is-invalid': genericValidator(project.descriptions, attemptedSubmit) === false || errorValidator('Descriptions') }"></vue-editor>
                            </div>
                        </div>
                    </div>
                    <!-- Action button + submitting -->
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary col-2 align-self-end" @click="submitClicked">{{
                        $t('acceptChanges') }}</CButton>
                    <!-- Loading -->
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
        <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup"
            v-model="showConfirmationPopup"></ConfirmationPopUp>
    </div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import router from '@/router/index';
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import Project from "@/classes/Project";
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import OptionsDropdown from '@/controls/OptionsDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'
import { API_HELPER } from "@/helpers/api.js";
import InputGetList from "@/classes/InputGetList";

export default {
    components: { LanguageDropdown, ConfirmationPopUp, VueEditor, GalleryPicker, OptionsDropdown, FormLoading },
    props: {
        baseProject:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            project: new Project({}, {}),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
        }
    },
    mounted: function () {
        this.resetProject();

        // Get users
        const itemsPerPage = 999;
        const currentPage = 1;
        const searchText = '';
        const sorterValue = 1; // Ascending
        const visible = true;

        // Get categories
        var input = new InputGetList(itemsPerPage, currentPage, searchText, sorterValue, "id", visible);
        this.getServices(input);

    },
    computed:
    {
        ...mapState('projects', ['putProjectOutput']),
        ...mapState('projects', ['postProjectOutput']),
        ...mapState('services', ['getServicesOutput']),
        ...mapState({
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        servicesInfo: function () {
            return this.getServicesOutput && this.getServicesOutput.data ? this.getServicesOutput.data : null;
        },
        servicesOptions: function () {
            var result = [];

            if (this.servicesInfo) {
                this.servicesInfo.forEach(service => {
                    result.push({ key: service.id, label: service.title });
                });
            }
            return result;
        },
        errorOutput: function () {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.project.names)) { if (!value) { result['Names'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.project.descriptions)) { if (!value) { result['Descriptions'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        name: function () {
            return this.baseProject || this.baseName ? (this.baseProject ? this.baseProject.name : this.baseName) : null;
        }
    },
    methods: {
        ...mapActions('services', ['getServices']),
        genericValidator,
        errorValidator,
        resetProject() {
            if (!this.baseProject) {
                var names = {}; var descriptions = {};
                CONSTANTS.supportedLanguages.forEach(language => { names[language.key] = ""; descriptions[language.key] = ""; });
                this.project = new Project(names, descriptions);
            }
            else {
                this.project = deepClone(new Project, this.baseProject);
                this.project.image = this.project.image ? { url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.project.image]) } : null;
                this.project.services = this.project.services.map(x => x.id);
            }
        },
        submitClicked() {
            this.attemptedSubmit = true;

            // Emit event
            if (this.project.isValid()) {
                this.submitting = true;
                this.$emit('submit-requested', this.project);
            }
        },
        onClickConfirmationPopup(value) {
            this.showConfirmationPopup = value;
            router.push({ name: 'ProjectsList' });
        },
        onProcessTask: function (val) {
            if (val != null) {
                if (val) {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        }
    },
    watch:
    {
        putProjectOutput: function (val) {
            this.onProcessTask(val);
        },
        postProjectOutput: function (val) {
            this.onProcessTask(val);
        },
        baseProject: function () {
            this.resetProject();
        },
        alertOutput: function () {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if (typeof this.alertOutput === 'string' || this.alertOutput instanceof String) {
                alert(this.alertOutput);
            }
        }
    }
}
</script> 
 
<style scoped>
@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container {
    border-color: #e55353;
}
</style> 